import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "react-hot-toast";

// Layout Components
import Navbar from "./components/Layout/Navbar";
import Footer from "./components/Layout/Footer";

// Page Components
import Home from "./pages/Home";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import GoogleCallback from "./pages/Auth/GoogleCallback";
import Profile from "./pages/Profile/Profile";
import Vendors from "./pages/Vendors/Vendors";
import VendorDetail from "./pages/Vendors/VendorDetail";
import MealPlans from "./pages/MealPlans/MealPlans";
import MealPlanDetail from "./pages/MealPlans/MealPlanDetail";
import Meals from "./pages/Meals/Meals";
import MealDetail from "./pages/Meals/MealDetail";
import MealOrder from "./pages/Meals/MealOrder";
import Orders from "./pages/Orders/Orders";
import OrderDetail from "./pages/Orders/OrderDetail";
import Subscriptions from "./pages/Subscriptions";
import Payments from "./pages/Payments";
import Checkout from "./pages/Checkout/Checkout";
import PaymentCallback from "./pages/Payment/PaymentCallback";
import PaymentError from "./pages/Payment/PaymentError";
import About from "./pages/About/About";
import Contact from "./pages/Contact/Contact";
import NotFound from "./pages/NotFound/NotFound";

// Protected Route Component
import ProtectedRoute from "./components/Auth/ProtectedRoute";

// Admin Components
import AdminLayout from "./components/Admin/AdminLayout";
import AdminProtectedRoute from "./components/Admin/AdminProtectedRoute";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import VendorManagement from "./pages/Admin/VendorManagement";
import EmployeeManagement from "./pages/Admin/EmployeeManagement";
import OrderManagement from "./pages/Admin/OrderManagement";
import Analytics from "./pages/Admin/Analytics";
import Settings from "./pages/Admin/Settings";
import AdminMealPlanManagement from "./pages/Admin/MealPlanManagement";
import AdminCreateMealPlan from "./pages/Admin/CreateMealPlan";
import AdminEditMealPlan from "./pages/Admin/EditMealPlan";
import AdminMealManagement from "./pages/Admin/MealManagement";
import AdminCreateMeal from "./pages/Admin/CreateMeal";
import AdminEditMeal from "./pages/Admin/EditMeal";
import NotificationManagement from "./pages/Admin/NotificationManagement";

// Vendor Components
import VendorLayout from "./components/Vendor/VendorLayout";
import VendorProtectedRoute from "./components/Vendor/VendorProtectedRoute";
import VendorDashboard from "./pages/Vendor/VendorDashboard";
import MealPlanManagement from "./pages/Vendor/MealPlanManagement";
import MealManagement from "./pages/Vendor/MealManagement";
import VendorSubscribers from "./pages/Vendor/VendorSubscribers";
import VendorOrders from "./pages/Vendor/VendorOrders";
import VendorMealPlanDetail from "./pages/Vendor/VendorMealPlanDetail";
import CreateMealPlan from "./pages/Vendor/CreateMealPlan";
import EditMealPlan from "./pages/Vendor/EditMealPlan";
import CreateMeal from "./pages/Vendor/CreateMeal";
import EditMeal from "./pages/Vendor/EditMeal";
import VendorSettings from "./pages/Vendor/VendorSettings";

// Styled Components
import styled from "styled-components";

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled(motion.main)`
  flex: 1;
  padding-top: ${(props) => (props.$isDashboard ? "0" : "80px")};

  @media (max-width: 768px) {
    padding-top: ${(props) => (props.$isDashboard ? "0" : "80px")};
  }
`;

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.3,
};

function App() {
  const location = useLocation();
  const isDashboardRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/vendor");

  return (
    <AppContainer>
      <Navbar />
      <MainContent $isDashboard={isDashboardRoute}>
        <AnimatePresence mode="wait">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Home />
                </motion.div>
              }
            />
            <Route
              path="/login"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Login />
                </motion.div>
              }
            />
            <Route
              path="/register"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Register />
                </motion.div>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <ForgotPassword />
                </motion.div>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <ResetPassword />
                </motion.div>
              }
            />
            <Route
              path="/auth/callback"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <GoogleCallback />
                </motion.div>
              }
            />
            <Route
              path="/vendors"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Vendors />
                </motion.div>
              }
            />
            <Route
              path="/vendors/:id"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <VendorDetail />
                </motion.div>
              }
            />
            <Route
              path="/meal-plans"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <MealPlans />
                </motion.div>
              }
            />
            <Route
              path="/meal-plans/:id"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <MealPlanDetail />
                </motion.div>
              }
            />
            <Route
              path="/meals"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Meals />
                </motion.div>
              }
            />
            <Route
              path="/meals/:id"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <MealDetail />
                </motion.div>
              }
            />
            <Route
              path="/meals/:id/order"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <MealOrder />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/about"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <About />
                </motion.div>
              }
            />
            <Route
              path="/contact"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Contact />
                </motion.div>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Profile />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Orders />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <OrderDetail />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Subscriptions />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Payments />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/:mealPlanId"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Checkout />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/callback"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <PaymentCallback />
                </motion.div>
              }
            />
            <Route
              path="/payment/error"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <PaymentError />
                </motion.div>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/vendors"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <VendorManagement />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/employees"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <EmployeeManagement />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <OrderManagement />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <NotificationManagement />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <Analytics />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <Settings />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/meal-plans"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminMealPlanManagement />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/meal-plans/create"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminCreateMealPlan />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/meal-plans/:id/edit"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminEditMealPlan />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/meals"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminMealManagement />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/meals/create"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminCreateMeal />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/meals/:id/edit"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminEditMeal />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />

            {/* Vendor Routes */}
            <Route
              path="/vendor"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <VendorDashboard />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meal-plans"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <MealPlanManagement />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/subscribers"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <VendorSubscribers />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/orders"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <VendorOrders />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meal-plans/create"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <CreateMealPlan />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meal-plans/:id"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <VendorMealPlanDetail />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meal-plans/:id/edit"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <EditMealPlan />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meals"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <MealManagement />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meals/create"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <CreateMeal />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/meals/:id/edit"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <EditMeal />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />
            <Route
              path="/vendor/settings"
              element={
                <VendorProtectedRoute>
                  <VendorLayout>
                    <VendorSettings />
                  </VendorLayout>
                </VendorProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route
              path="*"
              element={
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <NotFound />
                </motion.div>
              }
            />
          </Routes>
        </AnimatePresence>
      </MainContent>
      <Footer />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 4000,
            iconTheme: {
              primary: "#4ade80",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </AppContainer>
  );
}

export default App;
